import { FC, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { Box, Center } from '@chakra-ui/react';
import qs from 'query-string';

import {
  ToolboxOffersFilters,
  ToolboxOfferType,
  ToolboxTag,
} from '__generated__/GQL';
import SunshineLoader from 'components/_core/SunshineLoader';
import ErrorCard from 'components/ErrorCard';
import Loader from 'components/Loader';
import * as OfferDetailsLayout from 'layouts/OfferDetailsLayout';
import * as OffersLayout from 'layouts/OffersLayout';

import OfferCard from '../../components/OfferCard';
import TagsList from '../../components/TagsList';
import useGetToolboxOffers from '../../hooks/useGetToolboxOffers';
import useGetToolboxOffersCount from '../../hooks/useGetToolboxOffersCount';
import useGetToolboxTags from '../../hooks/useGetToolboxTags';
import * as routes from '../../routes';
import locales from './locales';

const QUERY_STRING_ARRAY_FORMAT = 'index';

const Offers: FC = () => {
  const location = useLocation();
  const history = useHistory();
  const firstRender = useRef(true);

  const [filters, setFilters] = useState<ToolboxOffersFilters>(
    qs.parse(location.search, {
      arrayFormat: QUERY_STRING_ARRAY_FORMAT,
    }),
  );

  const tags = useGetToolboxTags(ToolboxOfferType.Offer);

  const offers = useGetToolboxOffers({
    filters: { tagsIds: filters?.tagsIds },
  });

  const { count } = useGetToolboxOffersCount({ tagsIds: filters?.tagsIds });

  useEffect(() => {
    // We should not push to the history on the first render or else we will
    // have an issue with the browser's back button.
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }

    const urlParams = qs.stringify(filters, {
      arrayFormat: QUERY_STRING_ARRAY_FORMAT,
    });

    history.push(`${location.pathname}?${urlParams}`);
  }, [filters, history, location.pathname]);

  const onTagChange = (tag: ToolboxTag | null) => {
    setFilters({
      tagsIds: tag ? [tag.toolboxTagId] : undefined,
    });
  };

  if (offers.loading || tags.loading) {
    return <Loader />;
  }

  if (offers.error || tags.error) {
    return (
      <Center height="100%">
        <ErrorCard
          retry={() => Promise.all([offers.refetch(), tags.refetch()])}
        />
      </Center>
    );
  }

  return (
    <OffersLayout.PageContainer
      trackViewProps={{
        pageName: 'ToolboxOffers Viewed',
        usePrefix: false,
      }}
    >
      <OfferDetailsLayout.BackButton to={routes.root} />

      <OffersLayout.PageTitle
        count={count}
        description={locales.description}
        title={locales.title}
      />

      {tags ? (
        <TagsList
          activeTags={filters?.tagsIds}
          onChange={onTagChange}
          tags={tags.items}
        />
      ) : null}

      {offers.applyingFilters ? (
        <Box paddingY="space-48">
          <SunshineLoader />
        </Box>
      ) : null}

      {!offers.applyingFilters && offers.items ? (
        <OffersLayout.ListGrid paddingBottom="0">
          {offers.items.map((offer) => (
            <OfferCard.Link
              key={offer.toolboxOfferId}
              to={{
                pathname: routes.getOfferPath(offer.toolboxOfferId),
                state: { from: 'all-offers' },
              }}
            >
              <OfferCard partner={offer.partner} perkBadge={offer.perkBadge}>
                {offer.title}
              </OfferCard>
            </OfferCard.Link>
          ))}
        </OffersLayout.ListGrid>
      ) : null}

      {!offers.applyingFilters && offers.canFetchMore ? (
        <OffersLayout.ListFetchMoreButton
          isLoading={offers.fetchingMore}
          onClick={offers.fetchMore}
        >
          {locales.fetchMore}
        </OffersLayout.ListFetchMoreButton>
      ) : null}
    </OffersLayout.PageContainer>
  );
};

export default Offers;
